import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import React from 'react'

import { noop } from '@/app/definitions'

import FormControl from './form-control'

const secretHeaderNames = new Set(['apikey', 'api-key', 'authorization', 'cookie', 'proxy-authorization', 'x-api-key'])

export const isSecretHeaderName = (key = '') => {
  const normalizedKey = key.trim().toLowerCase()

  return (
    secretHeaderNames.has(normalizedKey) ||
    normalizedKey.includes('token') ||
    normalizedKey.includes('secret') ||
    normalizedKey.includes('password')
  )
}

type DynamicInputsProps = {
  editable?: boolean
  errors: Record<string, boolean>[]
  fields: string[]
  firstRowLock?: boolean
  name: string
  readOnly?: boolean
  values?: Record<string, string>[]
  addRow: () => void
  onBlur?: () => void
  onChange: (arg: { position: number; field: string; value: any }) => void
  onFocus?: () => void
  removeRow: (arg: { position: number }) => void
}

const emptyArr: [] = []

export default function DynamicInputs({
  editable = true,
  errors = emptyArr,
  fields = emptyArr,
  firstRowLock = false,
  name = '',
  readOnly = false,
  values = emptyArr,
  addRow = noop,
  onBlur = noop,
  onChange = noop,
  onFocus = noop,
  removeRow = noop,
  ...rest
}: DynamicInputsProps) {
  const [visiblePasswordFields, setVisiblePasswordFields] = React.useState<Record<string, boolean>>({})

  const togglePasswordVisibility = (inputKey: string) => {
    setVisiblePasswordFields((state) => ({
      ...state,
      [inputKey]: !state[inputKey],
    }))
  }

  return (
    <div>
      {values.map((row, i) => (
        <FormControl key={i}>
          {fields.map((field, j) => {
            const inputKey = `${name}-${i}-${field}`
            const resolvedType = field === 'value' && isSecretHeaderName(row.key) ? 'password' : 'text'
            const isPassword = resolvedType === 'password'
            const type = isPassword && visiblePasswordFields[inputKey] ? 'text' : resolvedType

            return (
              <TextField
                variant="standard"
                key={`${field}-${i}-${j}`}
                name={inputKey}
                label={field}
                error={!!errors[i][field]}
                disabled={!editable}
                value={row[field]}
                type={type}
                autoComplete="off"
                onFocus={onFocus}
                onBlur={onBlur}
                style={{
                  width: `${80 / fields.length}%`,
                  marginRight: '2%',
                }}
                slotProps={{
                  input: {
                    readOnly,
                    endAdornment: isPassword && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={visiblePasswordFields[inputKey] ? 'Hide secret' : 'Show secret'}
                          edge="end"
                          onClick={() => togglePasswordVisibility(inputKey)}
                          onMouseDown={(event) => event.preventDefault()}
                        >
                          {visiblePasswordFields[inputKey] ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                onChange={(e) => {
                  e.persist?.()
                  e.preventDefault()
                  onChange({
                    position: i,
                    field,
                    value: e.target.value,
                  })
                }}
                {...rest}
              />
            )
          })}
          <div
            style={{
              display: 'inline-block',
              textAlign: 'right',
              width: '16%',
            }}
          >
            {(!firstRowLock || i !== 0) && (
              <IconButton
                id={`${name}-remove-row-btn-${i}`}
                onClick={() => {
                  removeRow({ position: i })
                }}
              >
                <Icon>remove_circle</Icon>
              </IconButton>
            )}
            {i === values.length - 1 && (
              <IconButton id={`${name}-add-row-btn`} onClick={addRow}>
                <Icon>add_circle</Icon>
              </IconButton>
            )}
          </div>
        </FormControl>
      ))}
    </div>
  )
}
